/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  filterFEConfigDataCharacteristicStrategy,
  getIsServiceBasedMenuV2Enabled,
} from "constants/service_based_menu"
import { useEffect, useMemo, useRef } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { FEConfigData } from "store/duck/global.type"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { FEEDBACK } from "constants/routes"
import { useLoadConfigQuery } from "queries"
import { EventCategoryEng, EventActionEng } from "constants/tracking"
import { TroyWebTracker } from "utils/tracking"
import useRtkQueryMigrationEndpointsFlag from "./useRtkQueryMigrationEndpointsFlag"
import { isInventory } from "./useInventoryContext"

// singleton mutex to prevent duplicate dispatches
// issue: effects may be ran with old values from the store, esp. if they belong in the same render cycle
let isLoading = false

export interface UseFEConfigOptions {
  /** When set (e.g. review.merchantID on feedback), loads config for that merchant instead of menu state. */
  merchantID?: string
}

export default function useFEConfig(
  options?: UseFEConfigOptions,
): {
  data?: FEConfigData
  isLoading: boolean
  disallowedSOCKeys: Set<string>
  showAdditionalPriceDrawerLink?: boolean
  showPurchasability?: boolean
} {
  const {
    feConfig,
    merchantID: menuMerchantID,
    outletV2,
    isMenuGroup,
    menuGroupServiceBasedMenuConfig,
  } = useSelector(
    (s: AppState) => ({
      feConfig: s.global.feConfig,
      merchantID: s.menu.merchantID,
      outletV2: s.menu.outletV2,
      isMenuGroup: s.menu.isMenuGroup,
      menuGroupServiceBasedMenuConfig: s.menu.menuGroupServiceBasedMenuConfig,
    }),
    shallowEqual,
  )
  const dispatch = useDispatch()
  const location = useLocation()

  const isFeedbackPage = location.pathname?.startsWith(FEEDBACK)
  const isMenuGroupPage =
    isMenuGroup && !isInventory(location) && !isFeedbackPage // TODO: improve boolean to exclude future pages

  const effectiveMerchantID = options?.merchantID || menuMerchantID
  const isEnableServiceBasedMenu = getIsServiceBasedMenuV2Enabled(outletV2)
  // Removing feedback page condition as it has a multi-store layer, so callers must pass an explicit merchantID.
  const shouldLoadConfig =
    !isMenuGroupPage &&
    !!effectiveMerchantID &&
    (!!options?.merchantID || (!isFeedbackPage && isEnableServiceBasedMenu))

  const {
    flags: { loadConfig: isRtkLoadConfigEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  // Tracks the last merchantID for which the RTK tracking event was sent,
  // preventing duplicate fires when dependencies like isMenuGroupPage re-toggle.
  const rtkTrackingFiredForRef = useRef<string | null>(null)

  // RTK path reads directly from the query cache; legacy path still uses global.feConfig.
  const {
    currentData: rtkConfigData,
    isFetching: isRtkFetching,
    isLoading: isRtkLoading,
  } = useLoadConfigQuery(
    { merchantID: effectiveMerchantID || "" },
    {
      skip: isRtkFlagLoading || !isRtkLoadConfigEnabled || !shouldLoadConfig,
    },
  )

  // Old Redux path
  useEffect(() => {
    if (
      shouldLoadConfig &&
      effectiveMerchantID &&
      feConfig?.forMerchantID !== effectiveMerchantID &&
      !feConfig?.isLoading &&
      !isLoading &&
      !isRtkFlagLoading &&
      !isRtkLoadConfigEnabled
    ) {
      isLoading = true
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.loadConfig,
        labels: {
          MERCHANT_ID: effectiveMerchantID,
          IS_RTK_ENABLED: isRtkLoadConfigEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "loadConfig",
        },
      })
      const resp: any = dispatch(
        actionCreator.global.loadConfig(effectiveMerchantID),
      )
      resp.then?.(() => {
        isLoading = false
      })
    }
  }, [
    dispatch,
    feConfig,
    effectiveMerchantID,
    shouldLoadConfig,
    isRtkFlagLoading,
    isRtkLoadConfigEnabled,
  ])

  // RTK Query path tracking — fires once per merchantID when the RTK flag is on
  useEffect(() => {
    if (
      effectiveMerchantID &&
      !isRtkFlagLoading &&
      isRtkLoadConfigEnabled &&
      shouldLoadConfig &&
      rtkTrackingFiredForRef.current !== effectiveMerchantID
    ) {
      rtkTrackingFiredForRef.current = effectiveMerchantID
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.loadConfig,
        labels: {
          MERCHANT_ID: effectiveMerchantID,
          IS_RTK_ENABLED: isRtkLoadConfigEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "loadConfig",
        },
      })
    }
  }, [
    effectiveMerchantID,
    isRtkFlagLoading,
    isRtkLoadConfigEnabled,
    shouldLoadConfig,
  ])

  const activeFeConfigData = isRtkLoadConfigEnabled
    ? rtkConfigData
    : feConfig?.data

  // filter by outlet's supported SOCs
  const { filteredFEConfigData, disallowedSOCKeys } = useMemo(() => {
    const menuGroupFEConfigData = {
      serviceBasedMenuConfig: menuGroupServiceBasedMenuConfig,
    }
    // menu state config for menu group and global state config for outlet
    const feConfigData = isMenuGroupPage
      ? menuGroupFEConfigData
      : activeFeConfigData
    const characteristicStrategy = filterFEConfigDataCharacteristicStrategy(
      feConfigData,
      outletV2,
    )
    return characteristicStrategy
  }, [
    activeFeConfigData,
    outletV2,
    isMenuGroupPage,
    menuGroupServiceBasedMenuConfig,
  ])

  // isRtkFlagLoading must be checked first — while the GrabX flag is in flight,
  // both data paths are blocked, so we must stay in loading state regardless of isRtkLoadConfigEnabled.
  const computedIsLoading =
    shouldLoadConfig &&
    (isRtkFlagLoading ||
      (isRtkLoadConfigEnabled
        ? isRtkLoading || (!rtkConfigData && isRtkFetching)
        : feConfig?.isLoading ||
          feConfig?.forMerchantID !== effectiveMerchantID))

  return {
    isLoading: computedIsLoading,
    data: filteredFEConfigData,
    disallowedSOCKeys,
    showAdditionalPriceDrawerLink: !!filteredFEConfigData
      ?.serviceBasedMenuConfig?.characteristicStrategy?.pricingConfig.length,
    showPurchasability: !!filteredFEConfigData?.serviceBasedMenuConfig
      ?.characteristicStrategy?.purchasableConfig.length,
  }
}
